"use client";

import { useState, useRef, useEffect } from "react";
import { FaSearch, FaEye } from "react-icons/fa";
import Select from "../inventory/modules/Select/Select"; // Using the shared Select component
import RequestDetailModal from "./modal/RequestDetailModal";
import SupportRequestDetailModal from "./modal/SupportRequestDetailModal";
import AddRequestModal from "./modal/AddRequestModal";
import styles from "../../styles/inventory/technicalSupport.module.css";
import Button from "./modules/Button/Button";
import Badge from "./modules/Badge/Badge";
import SearchBar from "../shared-modules/SearchBar";

const StatusBadgeInTable = ({ currentStatus }) => {
  let variant = "default";
  switch (currentStatus) {
    case "open":
      variant = "warning";
      break;
    case "resolved":
      variant = "success";
      break;
    case "closed":
      variant = "danger";
      break;
    default:
      variant = "default";
  }

  return (
    <Badge variant={variant}>
      {currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)}
    </Badge>
  );
};

const TicketRow = ({
  ticket,
  onRowClick = () => {},
  onActionButtonClick = () => {},
}) => {
  const statusValue = ticket.resolved
    ? "resolved"
    : ticket.closed
    ? "closed"
    : "open";
  return (
    <div className={styles.row} onClick={() => onRowClick(ticket)}>
      <div className={styles.serial_number}>{ticket.serialNumber}</div>
      <div className={styles.status_cell}>
        <StatusBadgeInTable currentStatus={ticket.status} />
      </div>
      <div className={styles.device_type}>{ticket.type}</div>
      <div className={styles.issue_description}>{ticket.description}</div>
      <div className={styles.ticket_date}>{ticket.date}</div>
      <div className={styles.ticket_actions}>
        <button
          className={styles.view_history_btn}
          onClick={(e) => {
            e.stopPropagation();
            onActionButtonClick(ticket);
          }}
        >
          <FaEye className={styles.button_icon} />
          View events
        </button>
      </div>
    </div>
  );
};

const TenoviTechnicalSupportSection = ({
  supportTickets = [],
  onSearch = () => {},
  loading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [deviceFilter, setDeviceFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // State for SupportRequestDetailModal (row click)
  const [isSupportDetailModalOpen, setIsSupportDetailModalOpen] =
    useState(false);
  const [selectedSupportTicket, setSelectedSupportTicket] = useState(null);

  // State for RequestDetailModal (action button click)
  const [isRequestDetailModalOpen, setIsRequestDetailModalOpen] =
    useState(false);
  const [selectedRequestTicket, setSelectedRequestTicket] = useState(null);

  //   const [tickets, setTickets] = useState(
  //     [
  //     {
  //       id: 1,
  //       serialNumber: "IMEI-352094087982671",
  //       patientName: "Luis Romero",
  //       type: "Blood Pressure Monitor",
  //       contact: "0163 2300 0823 904",
  //       description:
  //         "BPM machine is malfunctioning needs service or replacement. Device shows error code E02 consistently.",
  //       date: "Dec 10, 2024",
  //       status: "open",
  //       email: "luis.romero@example.com",
  //       timeZone: "UTC +5",
  //       location: "New York", // Added location
  //     },
  //     {
  //       id: 2,
  //       serialNumber: "IMEI-352094087982672",
  //       patientName: "Maria Garcia",
  //       type: "Blood Glucose Monitor",
  //       contact: "0163 2300 0823 905",
  //       description: "Device not syncing with mobile app, readings not being recorded properly in the system.",
  //       date: "Dec 9, 2024",
  //     status: "resolved",
  //       email: "maria.garcia@example.com",
  //       timeZone: "UTC +5",
  //       location: "Los Angeles", // Added location
  //     },
  //     {
  //       id: 3,
  //       serialNumber: "IMEI-352094087982673",
  //       patientName: "John Smith",
  //       type: "Heart Rate Monitor",
  //       contact: "0163 2300 0823 906",
  //       description: "Heart rate readings are inconsistent and showing error messages during exercise monitoring.",
  //       date: "Dec 8, 2024",
  //     status: "closed",
  //       email: "john.smith@example.com",
  //       timeZone: "UTC +5",
  //       location: "Chicago", // Added location
  //     },
  //     {
  //       id: 4,
  //       serialNumber: "IMEI-352094087982674",
  //       patientName: "Sarah Johnson",
  //       type: "Oximeter Device",
  //       contact: "0163 2300 0823 907",
  //       description: "Device battery drains too quickly, needs replacement or repair. Battery lasts only 2 hours.",
  //       date: "Dec 7, 2024",
  //     status: "open",
  //       email: "sarah.johnson@example.com",
  //       timeZone: "UTC +5",
  //       location: "New York", // Added location
  //     },
  //     {
  //       id: 5,
  //       serialNumber: "IMEI-352094087982675",
  //       patientName: "Michael Brown",
  //       type: "Blood Pressure Monitor",
  //       contact: "0163 2300 0823 908",
  //       description: "Cuff inflation mechanism is not working properly, unable to get accurate readings.",
  //       date: "Dec 6, 2024",
  //     status: "resolved",
  //       email: "michael.brown@example.com",
  //       timeZone: "UTC +5",
  //       location: "Los Angeles", // Added location
  //     },
  //   ]
  // )

  const [tickets, setTickets] = useState([]);

  const deviceTypes = [...new Set(tickets.map((ticket) => ticket.type))];
  const deviceOptions = [
    { value: "", label: "All Devices" },
    ...deviceTypes.map((device) => ({ value: device, label: device })),
  ];

  const locations = [...new Set(tickets.map((ticket) => ticket.location))];
  const locationOptions = [
    { value: "", label: "All Locations" },
    ...locations.map((loc) => ({ value: loc, label: loc })),
  ];

  const statusOptionsFilter = [
    { value: "", label: "All Statuses" },
    { value: "open", label: "Open" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
  ];

  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch =
      ticket.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.contact.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.serialNumber.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDevice = deviceFilter === "" || ticket.type === deviceFilter;
    const matchesLocation =
      locationFilter === "" || ticket.location === locationFilter;
    const matchesStatus = statusFilter === "" || ticket.status === statusFilter;

    return matchesSearch && matchesDevice && matchesLocation && matchesStatus;
  });

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  const handleSearch = () => {
    onSearch(searchTerm);
  };

  const handleDeviceFilterChange = (value) => {
    setDeviceFilter(value);
  };

  const handleLocationFilterChange = (value) => {
    setLocationFilter(value);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
  };

  // Handler for row click (opens SupportRequestDetailModal)
  const handleRowClick = (ticket) => {
    setSelectedSupportTicket(ticket);
    setIsSupportDetailModalOpen(true);
  };

  // Handler for action button click (opens RequestDetailModal)
  const handleActionButtonClick = (ticket) => {
    setSelectedRequestTicket(ticket);
    setIsRequestDetailModalOpen(true);
  };

  const handleSupportDetailModalClose = () => {
    setIsSupportDetailModalOpen(false);
    setSelectedSupportTicket(null);
  };

  const handleRequestDetailModalClose = () => {
    setIsRequestDetailModalOpen(false);
    setSelectedRequestTicket(null);
  };

  if (loading) {
    return (
      <div className={styles.loading_container}>
        <div className={styles.loading_spinner}></div>
        <p className={styles.loading_text}>Loading support tickets...</p>
      </div>
    );
  }

  return (
    <div className={styles.technical_support_section}>
      <div className={styles.search_section}>
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onSearch={handleSearch}
          placeholder="Search here"
          maxWidth="300px"
        />
        <div className={styles.filter_container}>
          <Select
            options={deviceOptions}
            value={deviceFilter}
            onChange={handleDeviceFilterChange}
            placeholder="All Devices"
            size="small"
            width="180px"
          />
        </div>
        {/* <div className={styles.filter_container}>
          <Select
            options={locationOptions}
            value={locationFilter}
            onChange={handleLocationFilterChange}
            placeholder="All Locations"
            size="small"
            width="180px"
          />
        </div> */}
        <div className={styles.filter_container}>
          <Select
            options={statusOptionsFilter}
            value={statusFilter}
            onChange={handleStatusFilterChange}
            placeholder="All Statuses"
            size="small"
            width="180px"
          />
        </div>
      </div>

      <div className={styles.tickets_container}>
        <div className={styles.header}>
          <div className={styles.header_cell}>Serial Number</div>
          <div className={styles.header_cell}>Status</div>
          <div className={styles.header_cell}>Device Type</div>
          <div className={styles.header_cell}>Issue Description</div>
          <div className={styles.header_cell}>Date</div>
          <div className={styles.header_cell}>Actions</div>
        </div>

        {filteredTickets.length > 0 ? (
          filteredTickets.map((ticket) => (
            <TicketRow
              key={ticket.id}
              ticket={ticket}
              onRowClick={handleRowClick}
              onActionButtonClick={handleActionButtonClick}
            />
          ))
        ) : (
          <div className={styles.no_results}>
            <p className={styles.no_results_text}>
              No tickets found 
            </p>
          </div>
        )}
      </div>

      {selectedSupportTicket && (
        <SupportRequestDetailModal
          isOpen={isSupportDetailModalOpen}
          onClose={handleSupportDetailModalClose}
          ticket={selectedSupportTicket}
          currentPatient={{
            user: {
              first_name: selectedSupportTicket.patientName.split(" ")[0],
              last_name: selectedSupportTicket.patientName.split(" ")[1] || "",
              email: selectedSupportTicket.email,
              primary_phone: selectedSupportTicket.contact,
            },
          }}
          requestData={{
            description: selectedSupportTicket?.description || "",
          }}
          footerButtons={[]} // Default "Done" button
        />
      )}

      {selectedRequestTicket && (
        <RequestDetailModal
          isOpen={isRequestDetailModalOpen}
          onClose={handleRequestDetailModalClose}
          ticket={selectedRequestTicket}
          currentPatient={{
            user: {
              first_name: selectedRequestTicket.patientName.split(" ")[0],
              last_name: selectedRequestTicket.patientName.split(" ")[1] || "",
              email: selectedRequestTicket.email,
              primary_phone: selectedRequestTicket.contact,
            },
          }}
          requestData={{
            description: selectedRequestTicket?.description || "",
          }}
        />
      )}
    </div>
  );
};

export default TenoviTechnicalSupportSection;
